import React, { useState, Children } from 'react';
import { Header } from '../components/Header';
import { Calendar, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface InspectionConfirmedScreenProps {
  onFinish: () => void;
  onBack: () => void;
}
export function InspectionConfirmedScreen({
  onFinish,
  onBack
}: InspectionConfirmedScreenProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const handleFinish = () => {
    setShowConfirmation(true);
    window.setTimeout(() => {
      setShowConfirmation(false);
      onFinish();
    }, 3800);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <div className="h-full flex flex-col overflow-hidden relative" data-id="element-50">
      <div className="flex-none" data-id="element-51">
        <Header title="Your Inspection Is Confirmed" onBack={onBack} data-id="element-52" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 overflow-y-auto" data-id="element-53">
        <motion.p initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="text-sm text-gray-600 text-center mb-8 leading-relaxed" data-id="element-54">
          We'll Remind You 24h Before.
          <br data-id="element-55" />
          Full Details Are Available in{' '}
          <span className="font-semibold text-gray-900" data-id="element-56">My Profile</span>
        </motion.p>

        {/* Success Icon */}
        <div className="relative mb-10" data-id="element-57">
          <motion.div initial={{
          scale: 0.5,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 20,
          duration: 0.6
        }} className="relative z-10 w-24 h-24 bg-[#0F4C3A] rounded-full flex items-center justify-center shadow-lg" data-id="element-58">
            <Calendar className="w-11 h-11 text-white" strokeWidth={2} data-id="element-59" />
          </motion.div>

          {/* Pulse Ring */}
          <motion.div initial={{
          scale: 0.8,
          opacity: 0.5
        }} animate={{
          scale: 1.4,
          opacity: 0
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeOut',
          delay: 0.5
        }} className="absolute inset-0 bg-[#0F4C3A] rounded-full z-0" data-id="element-60" />

          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 15,
          delay: 0.4
        }} className="absolute -top-2 -right-2 w-8 h-8 bg-[#0F4C3A] rounded-full flex items-center justify-center border-2 border-white z-20 shadow-sm" data-id="element-61">
            <motion.svg initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 0.4,
            delay: 0.6,
            ease: 'easeOut'
          }} className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-62">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-63" />
            </motion.svg>
          </motion.div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col items-center w-full" data-id="element-64">
          <motion.div variants={itemVariants} className="text-center mb-10" data-id="element-65">
            <div className="text-3xl font-bold text-gray-900 mb-2 tracking-tight" data-id="element-66">
              October 8
            </div>
            <div className="text-lg font-medium text-gray-500" data-id="element-67">10:30 AM</div>
          </motion.div>

          <motion.div variants={itemVariants} className="w-full space-y-3" data-id="element-68">
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full border-2 border-[#0F4C3A] text-[#0F4C3A] py-3.5 rounded-xl font-bold hover:bg-[#0F4C3A] hover:text-white transition-colors shadow-sm" data-id="element-69">
              Add to calendar
            </motion.button>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer Button */}
      <div className="flex-none p-6 pt-2 pb-8 bg-[#F7F8F7]" data-id="element-70">
        <motion.button whileTap={{
        scale: showConfirmation ? 1 : 0.97
      }} onClick={handleFinish} disabled={showConfirmation} className="w-full bg-[#0F4C3A] text-white py-4 rounded-xl font-semibold text-lg hover:bg-[#134E3A] transition-colors shadow-md" data-id="element-71">
          Finish
        </motion.button>
      </div>

      {/* Transient completion confirmation */}
      <AnimatePresence data-id="element-72">
        {showConfirmation && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[1px]" data-id="element-73">
            <motion.div initial={{
          scale: 0.85,
          y: 8,
          opacity: 0
        }} animate={{
          scale: 1,
          y: 0,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 24
        }} role="status" aria-live="polite" className="bg-white rounded-3xl px-10 py-9 shadow-2xl flex flex-col items-center mx-6" data-id="element-74">
              <div className="relative mb-5" data-id="element-75">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 320,
              damping: 18,
              delay: 0.1
            }} className="relative z-10 w-24 h-24 bg-[#0F4C3A] rounded-full flex items-center justify-center shadow-lg" data-id="element-76">
                  <Check className="w-12 h-12 text-white" strokeWidth={3} data-id="element-77" />
                </motion.div>
                <motion.div initial={{
              scale: 0.8,
              opacity: 0.5
            }} animate={{
              scale: 1.5,
              opacity: 0
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 0.4
            }} className="absolute inset-0 bg-[#0F4C3A] rounded-full z-0" data-id="element-78" />
              </div>
              <p className="text-xl font-bold text-gray-900" data-id="element-79">
                Move-out complete!
              </p>
              <p className="text-sm text-gray-500 mt-1.5 text-center" data-id="element-80">
                Everything is set. We'll see you on inspection day.
              </p>

              {/* Animated DVORA logo sign-off */}
              <div className="mt-5 pt-4 border-t border-gray-100 w-full flex flex-col items-center" data-id="element-81">
                <div className="flex" aria-label="DVORA" data-id="element-82">
                  {'DVORA'.split('').map((letter, i) => <motion.span key={i} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.5 + i * 0.08,
                type: 'spring',
                stiffness: 320,
                damping: 20
              }} className="text-xl font-bold text-[#F2B705] tracking-wide" data-id="element-83">
                      {letter}
                    </motion.span>)}
                </div>
                <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1
            }} className="text-[10px] text-gray-400 mt-1 tracking-wide" data-id="element-84">
                  Residential Life, Simplified
                </motion.span>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}